import { FC, useEffect, useState } from "react";
import {
  AspectRatio,
  Box,
  Button,
  Card,
  Sheet,
  Stack,
  Typography,
} from "@mui/joy";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import logo from "src/assets/images/a-logo.png";
import { barItems } from "../organism/constant";

import TextImage1 from "src/assets/images/my-text.png";
import TextImage2 from "src/assets/images/bayad-barkhast.webp";
import TextImage3 from "src/assets/images/aghaye-shahid.webp";

export const Sidebar: FC = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const sidebarImages = [TextImage1, TextImage2, TextImage3];
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveImageIndex((prev) => (prev + 1) % sidebarImages.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [sidebarImages.length]);

  return (
    <Sheet
      component="aside"
      variant="plain"
      sx={{
        display: { xs: "none", lg: "flex" },
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        width: 300,
        flexShrink: 0,
        height: "100vh",
        p: 2,
        borderInlineEnd: "1px solid",
        borderColor: "divider",
        bgcolor: "background.surface",
        backdropFilter: "blur(18px)",
      }}
    >
      <Box sx={{ pt: 2 }}>
        <img
          src={logo}
          alt="logo"
          style={{ height: 220, objectFit: "cover" }}
        />
      </Box>

      <Stack spacing={1.25} width="100%">
        {barItems.map(({ title, icon: Icon, path }) => {
          const active = path === pathname;

          return (
            <Button
              key={title}
              component={Link as any}
              to={path}
              fullWidth
              size="lg"
              color={active ? "primary" : "neutral"}
              variant={active ? "soft" : "plain"}
              sx={{
                justifyContent: "flex-start",
                gap: 1.5,
                px: 2.5,
                py: 1.35,
                borderRadius: "18px",
                color: active ? "primary.500" : "text.secondary",
                "&:hover": {
                  bgcolor: active ? "primary.softBg" : "background.level1",
                },
              }}
            >
              <Icon style={{ fontSize: 26 }} />
              <Typography
                level="title-sm"
                noWrap
                sx={{ color: "inherit", fontWeight: active ? 900 : 750 }}
              >
                {t(title).charAt(0).toUpperCase() + t(title).slice(1)}
              </Typography>
            </Button>
          );
        })}
      </Stack>

      <Box
        sx={{
          width: "100%",
          borderRadius: "18px",
          bgcolor: "background.level1",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Card
          variant="outlined"
          sx={{
            p: 1,
            position: "relative",
            borderRadius: "18px",
            bgcolor: "background.surface",
            boxShadow: "lg",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <AspectRatio
            ratio="3/1.8"
            sx={{
              borderRadius: "18px",
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={sidebarImages[activeImageIndex]}
              alt={`sidebar slide ${activeImageIndex + 1}`}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transition: "opacity 0.4s ease",
              }}
            />
          </AspectRatio>

          <Box
            sx={{
              position: "absolute",
              bottom: 12,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              gap: 0.75,
            }}
          >
            {sidebarImages.map((_, index) => (
              <Box
                key={index}
                onClick={() => setActiveImageIndex(index)}
                sx={{
                  width: activeImageIndex === index ? 18 : 7,
                  height: 7,
                  borderRadius: "999px",
                  cursor: "pointer",
                  bgcolor:
                    activeImageIndex === index
                      ? "primary.500"
                      : "rgba(255,255,255,0.65)",
                  transition: "0.25s",
                }}
              />
            ))}
          </Box>
        </Card>
      </Box>
    </Sheet>
  );
};
